import { useState } from 'react';
import { CoverageSummary } from './routeTerritorial/CoverageSummary';
import { EntityPopupCard } from './routeTerritorial/EntityPopupCard';
import { LayerPanel, useLayerToggle } from './routeTerritorial/LayerPanel';
import type { SelectedFeature } from './routeTerritorial/layers';
import { RouteMetricsSummary } from './routeTerritorial/RouteMetricsSummary';
import { TerritorialMap } from './routeTerritorial/TerritorialMap';
import { useRouteTerritorialData } from './routeTerritorial/useRouteTerritorialData';
import './routeTerritorial/route-territorial.css';
import './ficha-territorial.css';

/**
 * Sección territorial de la Ficha — síntesis compacta (resumen + mapa
 * mediano). Consume el mismo núcleo (routeTerritorial/) que
 * LineOperationsPage; ninguna lógica de mapa/capas/fetch vive acá, solo el
 * layout específico de la Ficha.
 */
export function FichaTerritorialPanel({
  slug,
  token,
  analysisId,
  routeMode,
  ida,
  vuelta,
}: {
  slug: string;
  token: string;
  analysisId: string;
  routeMode: 'CIRCUIT' | null;
  ida: [number, number][];
  vuelta: [number, number][];
}): JSX.Element {
  const { direction, setDirection, isCircuit, routeCoordinates, routeMetrics, coverageEntities, error } =
    useRouteTerritorialData({ slug, token, analysisId, routeMode, ida, vuelta });
  const { activeLayers, toggleLayer } = useLayerToggle();
  const [selected, setSelected] = useState<SelectedFeature | null>(null);

  return (
    <section className="ficha-territorial">
      <div className="ft-header-row">
        <h3 className="ft-title">Recorrido y cobertura territorial</h3>
        <div className="ft-direction-tabs">
          {isCircuit ? (
            <button className="ft-tab ft-tab-active" disabled>
              CIRCUITO
            </button>
          ) : (
            <>
              <button className={direction === 'IDA' ? 'ft-tab ft-tab-active' : 'ft-tab'} onClick={() => setDirection('IDA')}>
                IDA
              </button>
              <button className={direction === 'VUELTA' ? 'ft-tab ft-tab-active' : 'ft-tab'} onClick={() => setDirection('VUELTA')}>
                VUELTA
              </button>
            </>
          )}
        </div>
      </div>

      {error && <p className="ft-error">{error}</p>}

      {routeMetrics && <RouteMetricsSummary metrics={routeMetrics} />}
      <CoverageSummary coverage={coverageEntities} />

      <div className="ft-map-wrap">
        <LayerPanel
          routeMetrics={routeMetrics}
          coverageEntities={coverageEntities}
          activeLayers={activeLayers}
          onToggle={toggleLayer}
        />
        {routeMetrics && routeCoordinates.length > 1 && (
          <TerritorialMap
            key={direction}
            routeCoordinates={routeCoordinates}
            direction={direction}
            isCircuit={isCircuit}
            coveragePolygon={coverageEntities?.coveragePolygon ?? null}
            showCoverage
            routeMetrics={routeMetrics}
            coverageEntities={coverageEntities}
            activeLayers={activeLayers}
            onSelectFeature={setSelected}
          />
        )}
      </div>

      {selected && <EntityPopupCard feature={selected} onClose={() => setSelected(null)} />}

      <p className="ft-note">
        Cobertura calculada a 5 min a pie (4.5 km/h) para la variante seleccionada — recalcula al cambiar
        IDA/VUELTA. Para ajustar tiempo/velocidad y explorar el mapa completo, ver el Visor de la línea.
      </p>
    </section>
  );
}
